/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
    segmentedButtonContainerVariants,
    segmentedButtonItemVariants,
    type SegmentedButtonContent,
} from '@/components/ui/segmented-button-styles';

/**
 * Segmented button — the styling layer from `segmented-button-styles.ts` bound to Radix
 * ToggleGroup with `type="single"` (CRCB2B-642). This is the default binding and the only one
 * built.
 *
 * ## Why ToggleGroup and not Tabs
 *
 * Both CRCB2B-629's and CRCB2B-642's Solution Notes suggest Radix Tabs. Rejected: none of the
 * designed uses owns a content panel, and Radix Tabs emits `role="tablist"` / `role="tab"` — a
 * `tab` with no associated `tabpanel` is an ARIA violation that would likely fail
 * `pnpm storybook:test --type=a11y`. ToggleGroup `type="single"` is the purpose-built
 * primitive: single selection among N, rendered as buttons, arrow-key roving focus, no
 * content-panel obligation.
 *
 * It works as a controlled component with a cookie-persisted value or a `useFetcher`-triggered
 * reload, which is what a view preference surviving across listing pages actually needs.
 *
 * ## Which primitive for which use case
 *
 * The deciding question is **does selecting a segment change the URL?**
 *
 * - **Yes → that is navigation.** Use a `NavLink` set with `aria-current`, not this component
 *   and not Tabs. Not built here.
 * - **No, and it sits in a form that must submit a value** → Radix RadioGroup (already
 *   installed) gives native form participation. Not built here. Note it would add *form
 *   participation only*: `type="single"` already announces as a radiogroup, so the role is not
 *   what a RadioGroup binding would buy.
 * - **No, otherwise** → this component.
 *
 * ## Accessible name is required
 *
 * `type="single"` renders `role="radiogroup"`, and a radiogroup with no name announces its
 * options without saying what they choose between. Pass `aria-label` or `aria-labelledby`.
 * Icon-only segments need their own `aria-label` too.
 */
/**
 * Tracks the selected segment's box so the pill can slide between segments instead of
 * cutting. Returns `null` until it has a real measurement.
 *
 * ## Why a measured thumb rather than CSS alone
 *
 * A moving pill needs to know where the target segment *is*, and segments are
 * content-width — no CSS construct can animate a background across sibling elements of
 * unknown width. So the offsets are read from layout.
 *
 * ## Why `useEffect` and not `useLayoutEffect`
 *
 * This is progressive enhancement, not a correctness fix: the selected item keeps its own
 * `bg-primary` until the thumb is ready, so there is nothing to flash. `useLayoutEffect`
 * would only add an SSR warning for no benefit.
 *
 * ## Why `width > 0` gates readiness
 *
 * jsdom reports `offsetWidth`/`offsetLeft` as 0 for everything. Without this gate a jsdom
 * render would mark the thumb ready, hand it a 0px box, and turn the selected item
 * transparent — the selected state would vanish from every snapshot and unit test. Gating on
 * a real measurement keeps the item's own fill wherever layout is unavailable, which is the
 * same fallback that serves pre-hydration HTML and a JS-disabled client.
 */
function useSelectedSegmentBox(rootRef: React.RefObject<HTMLDivElement | null>) {
    const [box, setBox] = useState<{ left: number; width: number } | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const measure = () => {
            const selected = root.querySelector<HTMLElement>(
                '[data-slot="segmented-button-item"][data-state="on"]'
            );
            setBox(
                selected && selected.offsetWidth > 0
                    ? { left: selected.offsetLeft, width: selected.offsetWidth }
                    : null
            );
        };

        measure();

        // Selection changes flip `data-state` rather than remounting, so a mutation observer
        // is what notices them — and it works for controlled and uncontrolled consumers alike,
        // without this component needing to know the value.
        const mutations = new MutationObserver(measure);
        mutations.observe(root, { subtree: true, attributes: true, attributeFilter: ['data-state'] });

        // Re-measure when the box changes for reasons unrelated to selection: container
        // resize, a late webfont shifting label widths, or a segment's label being swapped.
        const resizes = new ResizeObserver(measure);
        resizes.observe(root);
        for (const item of root.querySelectorAll('[data-slot="segmented-button-item"]')) {
            resizes.observe(item);
        }

        return () => {
            mutations.disconnect();
            resizes.disconnect();
        };
    }, [rootRef]);

    return box;
}

function SegmentedButton({
    className,
    children,
    ...props
}: Omit<React.ComponentProps<typeof ToggleGroup>, 'type' | 'value' | 'defaultValue' | 'onValueChange'> & {
    /** Single selection, so a plain string rather than ToggleGroup's `string | string[]` union. */
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
}) {
    const rootRef = useRef<HTMLDivElement>(null);
    const box = useSelectedSegmentBox(rootRef);

    return (
        <ToggleGroup
            ref={rootRef}
            // `type="single"` is fixed, not forwarded: multiple-selection would make this a
            // different control with a different role (`toolbar` + `aria-pressed`) and a
            // selected-pill treatment that means nothing.
            type="single"
            data-slot="segmented-button"
            // Segments read this to drop their own fill once the thumb is carrying it. Absent
            // until a real measurement exists, which is what keeps the un-hydrated and
            // no-JS renders correct.
            data-thumb={box ? 'ready' : undefined}
            className={cn(segmentedButtonContainerVariants(), className)}
            {...props}>
            {box ? (
                <span
                    data-slot="segmented-button-thumb"
                    // Purely decorative: the selected state is already announced by
                    // `aria-checked` on the segment, so exposing this would be noise.
                    aria-hidden="true"
                    // `inset-y-[3px]` matches the container padding exactly (see the arithmetic in
                    // `segmented-button-styles.ts`:
                    // 1px border + 3px padding = Figma's 4px inset), so the thumb is exactly the 40px
                    // segment height. Animating `translateX`/`width` keeps it on the
                    // compositor rather than relayouting the row on every selection.
                    className="pointer-events-none absolute inset-y-[3px] left-0 rounded-ui bg-primary transition-[transform,width] duration-200 ease-out motion-reduce:transition-none"
                    style={{ transform: `translateX(${box.left}px)`, width: box.width }}
                />
            ) : null}
            {children}
        </ToggleGroup>
    );
}

function SegmentedButtonItem({
    className,
    content,
    ...props
}: React.ComponentProps<typeof ToggleGroupItem> & { content?: SegmentedButtonContent }) {
    return (
        <ToggleGroupItem
            data-slot="segmented-button-item"
            className={cn(segmentedButtonItemVariants({ content }), className)}
            {...props}
        />
    );
}

export { SegmentedButton, SegmentedButtonItem };
